import React from 'react';
import { 
  Scale, 
  Stethoscope, 
  GraduationCap,
  Briefcase,
  Building,
  Users,
  ArrowRight,
  Star,
  Clock,
  Download,
  ExternalLink,
  Zap,
  Palette,
  Headphones,
  Wrench,
  Globe
} from 'lucide-react';

interface Template {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: React.ComponentType<any>;
  features: string[];
  industry: string;
  complexity: 'Simple' | 'Advanced' | 'Enterprise';
  rating: number;
  downloads: string;
  price: 'Free' | 'Pro' | 'Enterprise';
  previewUrl: string;
  demoUrl: string;
  thumbnailUrl?: string;
  status: 'Available' | 'Coming Soon' | 'Beta';
}

const templates: Template[] = [
  {
    id: 'legal-call-center',
    name: 'Legal Call Center',
    category: 'Legal Services',
    description: 'Complete video consultation system for law firms with client intake, attorney dashboard, and case management.',
    icon: Scale,
    features: [
      'Client intake forms',
      'Attorney dashboard',
      'Queue management',
      'Case notes & billing',
      'Document upload',
      'Attorney-client privilege'
    ],
    industry: 'Legal',
    complexity: 'Advanced',
    rating: 4.9,
    downloads: '1.2k',
    price: 'Pro',
    previewUrl: '/templates/legal/preview',
    demoUrl: '/templates/legal/demo',
    status: 'Available'
  },
  {
    id: 'healthcare-telemedicine',
    name: 'Healthcare Telemedicine',
    category: 'Healthcare',
    description: 'HIPAA-compliant telemedicine platform with patient records, prescription management, and secure consultations.',
    icon: Stethoscope,
    features: [
      'HIPAA compliance',
      'Patient records',
      'Prescription management',
      'Insurance verification',
      'Medical forms',
      'Secure messaging'
    ],
    industry: 'Healthcare',
    complexity: 'Enterprise',
    rating: 4.8,
    downloads: '890',
    price: 'Enterprise',
    previewUrl: '/templates/healthcare/preview',
    demoUrl: '/templates/healthcare/demo',
    status: 'Coming Soon'
  },
  {
    id: 'education-classroom',
    name: 'Virtual Classroom',
    category: 'Education',
    description: 'Interactive online learning platform with attendance tracking, breakout rooms, and student engagement tools.',
    icon: GraduationCap,
    features: [
      'Class scheduling',
      'Attendance tracking',
      'Breakout rooms',
      'Screen sharing',
      'Recording & playback',
      'Student engagement'
    ],
    industry: 'Education',
    complexity: 'Advanced',
    rating: 4.7,
    downloads: '2.1k',
    price: 'Pro',
    previewUrl: '/templates/education/preview',
    demoUrl: '/templates/education/demo',
    status: 'Coming Soon'
  },
  {
    id: 'interview-platform',
    name: 'Interview Platform',
    category: 'HR & Recruitment',
    description: 'Professional interview platform with candidate screening, recording, and collaborative evaluation tools.',
    icon: Briefcase,
    features: [
      'Candidate screening',
      'Interview recording',
      'Collaborative notes',
      'Skill assessments',
      'Calendar integration',
      'Feedback system'
    ],
    industry: 'HR',
    complexity: 'Simple',
    rating: 4.6,
    downloads: '756',
    price: 'Free',
    previewUrl: '/templates/interview/preview',
    demoUrl: '/templates/interview/demo',
    status: 'Coming Soon'
  },
  {
    id: 'sales-demo',
    name: 'Sales Demo Platform',
    category: 'Sales',
    description: 'Sales-focused video platform with CRM integration, proposal sharing, and deal tracking.',
    icon: Building,
    features: [
      'CRM integration',
      'Proposal sharing',
      'Deal tracking',
      'Lead scoring',
      'Follow-up automation',
      'Analytics dashboard'
    ],
    industry: 'Sales',
    complexity: 'Advanced',
    rating: 4.5,
    downloads: '432',
    price: 'Pro',
    previewUrl: '/templates/sales/preview',
    demoUrl: '/templates/sales/demo',
    status: 'Beta'
  },
  {
    id: 'customer-support',
    name: 'Customer Support',
    category: 'Support',
    description: 'Complete customer support platform with ticket management, live video support, and agent dashboard.',
    icon: Headphones,
    features: [
      'Ticket submission & management',
      'Agent dashboard & queue',
      'Live video support sessions',
      'Real-time chat messaging',
      'Screen sharing capabilities',
      'Customer satisfaction tracking'
    ],
    industry: 'Support',
    complexity: 'Advanced',
    rating: 4.8,
    downloads: '2.3k',
    price: 'Pro',
    previewUrl: '/templates/support/preview',
    demoUrl: '/templates/support/customer',
    status: 'Available'
  },
  {
    id: 'home-improvement',
    name: 'Home Improvement Call Center',
    category: 'Home Services',
    description: 'Mobile-optimized platform for home improvement contractors with project submission, photo uploads, and specialized contractor dashboard.',
    icon: Wrench,
    features: [
      'Mobile-optimized customer interface',
      'Photo upload & project details',
      'Contractor dashboard with filtering',
      'Project estimation tools',
      'Distance & value calculations',
      'Real-time project acceptance'
    ],
    industry: 'Home Services',
    complexity: 'Advanced',
    rating: 4.9,
    downloads: '856',
    price: 'Pro',
    previewUrl: '/templates/homeimprovement/preview',
    demoUrl: '/templates/homeimprovement/customer',
    status: 'Available'
  },
  {
    id: 'universal-agent',
    name: 'Universal Agent Dashboard',
    category: 'Multi-Template Management',
    description: 'Centralized dashboard for agents to handle requests from all templates (Legal, Support, Home Improvement) through a unified interface with external API integration.',
    icon: Globe,
    features: [
      'Multi-template request management',
      'External API integration (Zapier/webhooks)',
      'Universal request queue & filtering',
      'Cross-template session handling',
      'Real-time request notifications',
      'Consolidated analytics & reporting'
    ],
    industry: 'Universal',
    complexity: 'Enterprise',
    rating: 4.8,
    downloads: '234',
    price: 'Pro',
    previewUrl: '/templates/universal/preview',
    demoUrl: '/templates/universal/dashboard',
    status: 'Available'
  },
  {
    id: 'general-template',
    name: 'General Template',
    category: 'General Purpose',
    description: 'Simple and flexible template for general customer interactions with basic contact form and agent dashboard.',
    icon: Users,
    features: [
      'Simple contact form',
      'Basic customer information collection',
      'Agent dashboard for request management',
      'Real-time notifications',
      'Queue management',
      'Clean, minimal interface'
    ],
    industry: 'General',
    complexity: 'Simple',
    rating: 4.7,
    downloads: '45',
    price: 'Free',
    previewUrl: '/templates/general/demo',
    demoUrl: '/templates/general/demo',
    status: 'Available'
  }
];

interface TemplatesPageProps {
  onNavigate?: (path: string) => void;
}

export const TemplatesPage: React.FC<TemplatesPageProps> = ({ onNavigate }) => {
  const getStatusBadge = (status: Template['status']) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800';
      case 'Coming Soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'Beta':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriceBadge = (price: Template['price']) => {
    switch (price) {
      case 'Free':
        return 'bg-green-100 text-green-800';
      case 'Pro':
        return 'bg-blue-100 text-blue-800';
      case 'Enterprise':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplexityColor = (complexity: Template['complexity']) => {
    switch (complexity) {
      case 'Simple':
        return 'text-green-600';
      case 'Advanced':
        return 'text-yellow-600';
      case 'Enterprise':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const availableTemplates = templates.filter(t => t.status === 'Available');
  const comingSoonTemplates = templates.filter(t => t.status !== 'Available');

  return (
    <div>
      {/* Header */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg mb-6">
        <div className="px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">
              Video Call Templates
            </h1>
            <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
              Ready-to-deploy video calling solutions for every industry. 
              Get your customers up and running in minutes with professional, 
              customizable templates.
            </p>
          </div>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 dark:bg-blue-900/20 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Zap className="text-blue-600 dark:text-blue-400" size={24} />
              </div>
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">5-Minute Setup</h3>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Deploy professional video calling in minutes</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900/20 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Building className="text-green-600 dark:text-green-400" size={24} />
              </div>
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">Industry-Specific</h3>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Built for your industry's unique needs</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Download className="text-purple-600 dark:text-purple-400" size={24} />
              </div>
              <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">Fully Customizable</h3>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Brand it, configure it, make it yours</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* Available Templates */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Available Now</h2>
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              {availableTemplates.length} template{availableTemplates.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableTemplates.map((template) => (
              <div key={template.id} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg border border-light-border dark:border-dark-border overflow-hidden hover:shadow-xl transition-shadow">
                {/* Template Header */}
                <div className="p-6 border-b border-light-border dark:border-dark-border">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded-lg mr-4">
                        <template.icon className="text-blue-600 dark:text-blue-400" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">{template.name}</h3>
                        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{template.category}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(template.status)}`}>
                        {template.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriceBadge(template.price)}`}>
                        {template.price}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">{template.description}</p>

                  <div className="flex items-center space-x-4 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    <div className="flex items-center">
                      <Star className="text-yellow-400 mr-1" size={12} />
                      {template.rating}
                    </div>
                    <div className="flex items-center">
                      <Download className="mr-1" size={12} />
                      {template.downloads}
                    </div>
                    <div className={`${getComplexityColor(template.complexity)}`}>
                      {template.complexity}
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="p-6">
                  <h4 className="font-medium text-gray-900 mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {template.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                    {template.features.length > 4 && (
                      <li className="text-sm text-light-text-secondary dark:text-dark-text-secondary opacity-60">
                        +{template.features.length - 4} more features
                      </li>
                    )}
                  </ul>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6">
                  <div className="space-y-3">
                    {/* Primary Action */}
                    <button
                      onClick={() => {
                        const baseUrl = window.location.origin;
                        const orgId = window.location.pathname.split('/')[1];
                        const fullUrl = orgId && orgId !== 'templates' 
                          ? `${baseUrl}/${orgId}${template.demoUrl}`
                          : `${baseUrl}${template.demoUrl}`;
                        window.open(fullUrl, '_blank');
                      }}
                      className="w-full bg-blue-600 dark:bg-blue-500 text-white text-center py-3 px-4 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium flex items-center justify-center"
                    >
                      Try Live Demo
                      <ExternalLink size={16} className="ml-2" />
                    </button>
                    
                    {/* Role-based Previews */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          const baseUrl = window.location.origin;
                          const orgId = window.location.pathname.split('/')[1];
                          let agentUrl = '';
                          if (template.id.includes('legal')) {
                            agentUrl = '/templates/legal/dashboard';
                          } else if (template.id.includes('support') || template.id.includes('customer-support')) {
                            agentUrl = '/templates/support/dashboard';
                          } else if (template.id.includes('home-improvement')) {
                            agentUrl = '/templates/homeimprovement/dashboard';
                          } else if (template.id.includes('universal-agent')) {
                            agentUrl = '/templates/universal/dashboard';
                          } else {
                            agentUrl = `/templates/${template.id.split('-')[0]}/dashboard`;
                          }
                          const fullUrl = orgId && orgId !== 'templates' 
                            ? `${baseUrl}/${orgId}${agentUrl}`
                            : `${baseUrl}${agentUrl}`;
                          window.open(fullUrl, '_blank');
                        }}
                        className="flex-1 bg-green-600 dark:bg-green-500 text-white text-center py-2 px-3 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors text-sm font-medium flex items-center justify-center"
                      >
                        Agent View
                        <ExternalLink size={12} className="ml-1" />
                      </button>
                      <button
                        onClick={() => {
                          const baseUrl = window.location.origin;
                          const orgId = window.location.pathname.split('/')[1];
                          let customerUrl = '';
                          if (template.id.includes('legal')) {
                            customerUrl = '/templates/legal/client';
                          } else if (template.id.includes('support') || template.id.includes('customer-support')) {
                            customerUrl = '/templates/support/customer';
                          } else if (template.id.includes('home-improvement')) {
                            customerUrl = '/templates/homeimprovement/customer';
                          } else if (template.id.includes('universal-agent')) {
                            customerUrl = '/templates/universal/request';
                          } else {
                            customerUrl = `/templates/${template.id.split('-')[0]}/customer`;
                          }
                          const fullUrl = orgId && orgId !== 'templates' 
                            ? `${baseUrl}/${orgId}${customerUrl}`
                            : `${baseUrl}${customerUrl}`;
                          window.open(fullUrl, '_blank');
                        }}
                        className="flex-1 bg-orange-600 dark:bg-orange-500 text-white text-center py-2 px-3 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors text-sm font-medium flex items-center justify-center"
                      >
                        Customer View
                        <ExternalLink size={12} className="ml-1" />
                      </button>
                    </div>
                    
                    {/* Branding */}
                    <button
                      onClick={() => {
                        let brandingUrl = '/templates/';
                        if (template.id.includes('legal')) {
                          brandingUrl += 'legal/branding';
                        } else if (template.id.includes('support') || template.id.includes('customer-support')) {
                          brandingUrl += 'support/branding';
                        } else if (template.id.includes('home-improvement')) {
                          brandingUrl += 'homeimprovement/branding';
                        } else if (template.id.includes('universal-agent')) {
                          brandingUrl += 'universal/branding';
                        } else if (template.id.includes('general-template')) {
                          brandingUrl += 'general/branding';
                        } else {
                          brandingUrl += `${template.category.toLowerCase()}/branding`;
                        }
                        window.history.pushState({}, '', brandingUrl);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="w-full bg-purple-600 dark:bg-purple-500 text-white text-center py-2 px-3 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors text-sm font-medium flex items-center justify-center"
                    >
                      <Palette size={14} className="mr-2" />
                      Customize Branding
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Coming Soon Templates */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Coming Soon</h2>
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              {comingSoonTemplates.length} template{comingSoonTemplates.length !== 1 ? 's' : ''} in development
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {comingSoonTemplates.map((template) => (
              <div key={template.id} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl shadow-lg border border-light-border dark:border-dark-border overflow-hidden opacity-75">
                {/* Template Header */}
                <div className="p-6 border-b border-light-border dark:border-dark-border">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary p-3 rounded-lg mr-4">
                        <template.icon className="text-light-text-secondary dark:text-dark-text-secondary" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">{template.name}</h3>
                        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{template.category}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(template.status)}`}>
                        {template.status}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">{template.description}</p>

                  <div className="flex items-center space-x-4 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    <div className={`${getComplexityColor(template.complexity)}`}>
                      {template.complexity}
                    </div>
                    <div className="flex items-center">
                      <Clock className="mr-1" size={12} />
                      Q2 2024
                    </div>
                  </div>
                </div>

                {/* Features Preview */}
                <div className="p-6">
                  <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary mb-3">Planned Features</h4>
                  <ul className="space-y-2">
                    {template.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        <div className="w-1.5 h-1.5 bg-light-text-secondary dark:bg-dark-text-secondary rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="px-6 pb-6">
                  <button className="w-full bg-gray-100 text-gray-500 py-2 px-4 rounded-lg text-sm font-medium cursor-not-allowed">
                    Notify When Available
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Need a Custom Template?</h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Can't find exactly what you need? Our team can create custom video calling 
            solutions tailored to your specific industry and requirements.
          </p>
          <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
            Request Custom Template
          </button>
        </div>
      </div>
    </div>
  );
};